import { useState } from 'react'

import { Box, Icon, StatusLabel, Text } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  ProjectionKind,
  usePaymentExecutionTransfersDataTable,
} from 'generated/resources/datatable.graphql'
import {
  BankingTransferFilter,
  BankingValueObjectsTransferStatusEnum,
  PaymentExecutionDetailFragment,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import i18n from 'modules/core/lib/i18n'

import { AccountNumber } from 'modules/banking/components/TransfersDataTable/_AccountNumber'
import { Amount } from 'modules/banking/components/TransfersDataTable/_Amount'
import { Beneficiary } from 'modules/banking/components/TransfersDataTable/_Beneficiary'
import { Requester } from 'modules/banking/components/TransfersDataTable/_Requester'
import { SourceType } from 'modules/banking/components/TransfersDataTable/_SourceType'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'

import { DateLabel } from './../PaymentsDataTable/_DateLabel'
import { Actions } from './_Actions'

export const PaymentTransfersDataTable = ({
  paymentExecution,
  onRevokeTransfer,
  legalEntitiesOptions,
  requestersOptions,
  seeStatusColumn = false,
}: {
  paymentExecution: PaymentExecutionDetailFragment
  onRevokeTransfer?: (id: number) => void
  legalEntitiesOptions: { value: number; label: string }[]
  requestersOptions: { value: number; label: string }[]
  seeStatusColumn?: boolean
}) => {
  const [filter, setFilter] = useState('')
  const areTransfersRevocable = paymentExecution.status === 'init'

  const source = usePaymentExecutionTransfersDataTable(
    [
      {
        identifier: 'beneficiary',
        title: i18n.t('banking.attributes.beneficiary'),
        kind: ProjectionKind.Custom,
        sortBy: 'beneficiaryName',
        template: '{{ beneficiaryName }}',
        render: ({ beneficiaryName }) => {
          return <Beneficiary name={beneficiaryName} />
        },
      },
      {
        identifier: 'requester',
        title: i18n.t('banking.attributes.requester'),
        kind: ProjectionKind.Custom,
        template: '{{ requester.fullName }}',
        render: ({ requester }) => {
          return <Requester requester={requester} />
        },
      },
      {
        identifier: 'type',
        title: i18n.t('banking.attributes.type'),
        kind: ProjectionKind.Custom,
        sortBy: 'sourceType',
        template: '{{ type }}',
        render: ({ sourceType }) => {
          return <SourceType type={sourceType} />
        },
      },
      {
        identifier: 'amount',
        title: i18n.t('banking.attributes.amount'),
        kind: ProjectionKind.Custom,
        sortBy: 'amountCents',
        template: '{{ amountCents }}',
        render: ({ amountCents, currency }) => {
          return <Amount amountCents={amountCents} currency={currency} />
        },
      },
      {
        identifier: 'dueDate',
        title: i18n.t('banking.attributes.due_date'),
        kind: ProjectionKind.Custom,
        sortBy: 'dueDate',
        template: '{{ dueDate }}',
        render: ({ dueDate }) => {
          return <DateLabel date={dueDate} />
        },
      },
      {
        identifier: 'iban',
        title: i18n.t('banking.attributes.iban'),
        sortBy: 'accountNumber',
        kind: ProjectionKind.Custom,
        template: '{{ iban }}',
        render: ({ accountNumber }) => {
          return <AccountNumber accountNumber={accountNumber} />
        },
      },
      ...(seeStatusColumn
        ? [
            {
              identifier: 'status',
              title: i18n.t('banking.attributes.status'),
              kind: ProjectionKind.Custom,
              template: '{{ status }}',
              render: (row: { status?: BankingValueObjectsTransferStatusEnum | null }) => {
                const status = row.status || 'init'
                return (
                  <Box flexDirection="row" alignItems="center" gap="s8">
                    <StatusLabel type={statusLabelType(status)}>{statusText(status)}</StatusLabel>
                  </Box>
                )
              },
            },
          ]
        : []),
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 50,
        render: ({ id, sourceId, sourceType }) => {
          return (
            <Actions
              revocable={areTransfersRevocable}
              onRevoke={() => onRevokeTransfer?.(id)}
              sourceType={sourceType}
              sourceId={sourceId}
            />
          )
        },
      },
    ],
    {
      variables: {
        paymentExecutionId: paymentExecution.id,
        search: filter,
      },
    }
  )

  const filters: DataTableFilter<BankingTransferFilter>[] = [
    {
      identifier: 'due_date',
      title: i18n.t('banking.transfers.data_table.filters.due_date'),
      path: 'dueDate',
      type: FilterType.Date,
      categories: [
        {
          kind: 'DatePicker',
          operation: 'between',
        },
      ],
    },
    {
      identifier: 'legal_entity_id',
      title: i18n.t('banking.transfers.data_table.filters.legal_entity'),
      path: 'legalEntityId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: legalEntitiesOptions,
        },
      ],
    },
    {
      identifier: 'type',
      title: i18n.t('banking.transfers.data_table.filters.type'),
      path: 'sourceType',
      type: FilterType.String,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              value: 'Finance::Invoice',
              label: i18n.t('banking.transfers.source_types.invoices'),
            },
            {
              value: 'Expenses::Expensable',
              label: i18n.t('banking.transfers.source_types.expensables'),
            },
          ],
        },
      ],
    },
    {
      identifier: 'requester',
      title: i18n.t('banking.transfers.data_table.filters.requester'),
      path: 'requesterId',
      type: FilterType.Integer,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: requestersOptions,
        },
      ],
    },
  ]

  const EmptyState = (
    <>
      <Icon.Large icon="document-folder-drawer-2" color="grey600" />
      <Box flexDirection="column" gap="s4" alignItems="center">
        <Text.Header2>{i18n.t('banking.transfers.data_table.empty_state.title')}</Text.Header2>
      </Box>
    </>
  )

  return (
    <DataTable
      disableExport
      source={source}
      persistFilters={false}
      sort={{ path: 'created_at', direction: 'desc' }}
      pagination={{
        kind: 'offset',
        itemsPerPage: 25,
      }}
      filters={filters}
      search={{
        value: filter,
        onChange: (ev) => setFilter(ev.target.value),
        placeholder: i18n.t('banking.transfers.data_table.search_placeholder'),
      }}
      emptyState={EmptyState}
    />
  )
}
